import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ContentCopy,
    Delete,
    Visibility,
    Launch,
    Timer,
    CalendarToday,
} from '@mui/icons-material';

import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {formatDateTime2, copyToClipboard} from "@/utils/helpers";
import { useTranslation } from "@/hooks/useTranslation";
export const STATUS_COLORS = {
    SCHEDULED: 'primary',
    STARTED: 'success',
    ENDED: 'default',
    CANCELLED: 'error'
};
const MeetingCard = ({ meeting, onDelete }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    if (!meeting) {
        return null;
    }
    const handleCopyLink = async () => {
        const success = await copyToClipboard(meeting.joinUrl);
        if (success) {
            toast.success(t("interviews.toasts.linkCopied"));
        } else {
            toast.error(t("interviews.toasts.copyFailed"));
        }
    };

    const handleJoinMeeting = () => {
        window.open(meeting.joinUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDelete = () => {
        if (window.confirm(t("interviews.card.deleteConfirm", { topic: meeting.topic }))) {
            onDelete(meeting.id);
        }
    };

    return (
        <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {meeting.topic}
                    </Typography>
                    <Chip
                        label={meeting.status}
                        color={STATUS_COLORS[meeting.status] || 'default'}
                        size="small"
                    />
                </Box>

                {meeting.agenda && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {meeting.agenda}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        {formatDateTime2(meeting.startTime)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Timer sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        {t("focusGroups.card.durationValue", { count: meeting.duration })}
                    </Typography>
                </Box>

                {meeting.password && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t("interviews.card.passwordLabel")} <strong>{meeting.password}</strong>
                        </Typography>
                    </Box>
                )}
            </CardContent>

            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t("interviews.card.copyLink")}>
                        <IconButton size="small" color="primary" onClick={handleCopyLink}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t("interviews.card.viewDetails")}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/interviews/${meeting.id}`)}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t("interviews.card.deleteMeeting")}>
                        <IconButton size="small" color="error" onClick={handleDelete}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Launch />}
                    onClick={handleJoinMeeting}
                    disabled={meeting.status === 'CANCELLED'}
                >
                    {t("interviews.card.join")}
                </Button>
            </CardActions>
        </Card>
    );
};

export default MeetingCard;